import styled from 'styled-components/macro';
import * as colors from 'src/styles/colors';
import { useStore } from 'src/hooks/useStore';
import { mobilePoint } from 'src/styles/globalStyle';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormType, PrimaryColors } from 'src/types/globalTypes';
import { IResPage } from 'src/types/api/pageTypes';
import DetailBanner from 'src/components/layout/banner/DetailBanner';
import Breadcrumb from 'src/components/layout/breadcrumb/Breadcrumb';
import ReactSelect, { IOption } from 'src/components/ui-kit/ReactSelect';
import { FaThList } from 'react-icons/fa';
import { BsTable } from 'react-icons/bs';
import Tooltip from 'src/components/ui-kit/Tooltip';
import Pagination from 'src/components/layout/pagination/Pagination';
import NotFoundPage from 'src/components/layout/notFound/NotFoundPage';
import PlaceImg from 'src/components/ui-kit/PlaceImg';
import ToTop from 'src/components/ui-kit/ToTop';
import { IReqPatentRightsPage } from 'src/types/api/patentRightsApiTypes';
import { IReqTmarkRightsPage } from 'src/types/api/trademarkRightsApiTypes';

const ImgDiv = styled.div`
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  position: absolute;
  z-index: -1;
  @media (max-width: ${mobilePoint}) {
    display: none;
  }
`;
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 39px;
  height: max-content;
`;
const FunDiv = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  width: 80%;
  justify-content: space-between;
  font-size: 14px;
  padding: 20px 0;
  color: ${colors.BlueGray_600};
`;
const FormChange = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const SelectTitle = styled.div`
  color: ${colors.BlueGray_300};
`;
const Icon = styled.div`
  cursor: pointer;
`;
const InnerWrapper = styled.div`
  align-items: center;
  width: 80%;
  background-color: ${colors.white};
  border-radius: 8px;
  @media (max-width: ${mobilePoint}) {
    width: 95%;
    height: max-content;
    top: 14%;
    text-align: center;
  }
`;
const Form = styled.div`
  width: 100%;

  @media (max-width: ${mobilePoint}) {
    width: 100%;
    height: fit-content;
  }
`;
const MobileSubTitle = styled.div`
  font-family: NotoSansTC-Medium;
  font-size: 16px;
`;

// 結果頁面輸出共用格式
export const Table = styled.div`
  font-family: NotoSansTC-Medium;
  font-size: 14px;
`;
export const Th = styled.div`
  padding: 0.5em 1em;
  color: ${colors.BlueGray_600};
  font-size: 15px;
`;
export const Td = styled.div`
  padding: 1em 1em;
  word-break: break-all;
`;

export const ListWrapper = styled.div`
  flex: 1;
  flex-direction: column;
  justify-content: space-evenly;
  font-size: 14px;
`;
export const ListRow = styled.div<{ bgcolor: string }>`
  display: grid;
  grid-template-columns: 80px 3fr 1fr;
  padding: 18px 32px;
  gap: 49px;
  border-bottom: 1px solid ${(prop) => prop.bgcolor};
  cursor: pointer;
  :hover {
    background-color: ${(prop) => prop.bgcolor};
  }
`;
export const TypeItem = styled.div``;
export const ListContent = styled.div`
  font-family: NotoSansTC-Medium;
  display: grid;
  gap: 14px;
`;
export const ListItem = styled.div`
  display: flex;
`;
export const ListTitle = styled.div`
  color: ${colors.BlueGray_500};
  white-space: nowrap;
`;
export const ListItemContent = styled.div``;

interface Iprops {
  title: string;
  children: React.ReactNode;
  bannerBgImg: string;
  image: string;
  imgDot: string;
  bgColor: PrimaryColors;
  returnPageUrl: string;
  tip?: React.ReactNode;
  orderOptions: IOption[];
  page: IResPage | undefined;
  formType: FormType;
  setFormType: (type: FormType) => void;
  pageNo: number;
  setPageNo: (pageNo: number) => void;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  orderCondition: string | number;
  setOrderCondition: (orderCondition: string | number) => void;
  reqParams?: IReqPatentRightsPage | IReqTmarkRightsPage;
}

/**
 * 搜尋結果頁面元件
 */
const SearchResult: React.FC<Iprops> = ({
  title,
  children,
  bannerBgImg,
  bgColor,
  image,
  imgDot,
  returnPageUrl,
  tip,
  orderOptions,
  page,
  formType,
  setFormType,
  pageNo,
  setPageNo,
  pageSize,
  setPageSize,
  orderCondition,
  setOrderCondition,
  reqParams,
}) => {
  const isMobile = useStore((state) => state.isMobile);
  const returnPageName = '搜尋頁';
  const mobileSubTitle = '搜尋結果';
  const isNotFound = page?.dataCount === 0;
  const navigate = useNavigate();
  
  const handleKeyDown = (
    e: React.KeyboardEvent<Element>, 
    callback: () => void
  ): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  };

  // 使用 useEffect 在 pageNo 更新時同步更新網址欄的參數
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('pageNo', String(pageNo));
    searchParams.set('pageSize', String(pageSize));
    navigate(`?${searchParams.toString()}`);
  }, [pageNo, pageSize, navigate]);

  // 電腦版功能列
  const desktopFun = (
    <FunDiv>
      <Breadcrumb returnPageName={returnPageName} returnPageUrl={returnPageUrl} />
      <FormChange>
        <SelectTitle>排序</SelectTitle>
        <ReactSelect
          options={orderOptions}
          onChange={(value) => {
            setOrderCondition(value);
          }}
          value={orderCondition}
          isPill={true}
          primaryColors={bgColor}
          ariaLabelValue="選擇後將會排序清單"
        />
        ｜
        <Icon 
		tabIndex={0} 
		role="button" 
		aria-label="列表式檢視" 
		aria-pressed={formType === FormType.list}
		onClick={() => setFormType(FormType.list)} 
		onKeyDown={(e) => handleKeyDown(e, () => setFormType(FormType.list))} 
		>
          <Tooltip overlay={FormType.list} bgColor={bgColor.sixth}>
            <FaThList
              color={formType === FormType.list ? bgColor.seventh : colors.BlueGray_300}
            />
          </Tooltip>
        </Icon>
        <Icon 
		tabIndex={0} 
		role="button" 
		aria-label="表格式檢視" 
		aria-pressed={formType === FormType.table}
		onClick={() => setFormType(FormType.table)} 
		onKeyDown={(e) => handleKeyDown(e, () => setFormType(FormType.table))}
		>
          <Tooltip overlay={FormType.table} bgColor={bgColor.sixth}>
            <BsTable
              color={formType === FormType.table ? bgColor.seventh : colors.BlueGray_300}
            />
          </Tooltip>
        </Icon>
      </FormChange>
    </FunDiv>
  );

  // 手機板功能列
  const mobileFun = (
    <FunDiv>
      <MobileSubTitle>{mobileSubTitle}</MobileSubTitle>
      <FormChange>
        <ReactSelect
          options={orderOptions}
          onChange={(value) => {
            setOrderCondition(value);
          }}
          value={orderCondition}
          isPill={true}
          primaryColors={bgColor}
        />
      </FormChange>
    </FunDiv>
  );

  return (
    <>
      {isMobile && <ToTop />}
      {!isMobile && (
        <ImgDiv>
          <PlaceImg image={imgDot} placeProps={{ height: '80px', top: '20%', right: '0%' }} />
          <PlaceImg image={imgDot} placeProps={{ height: '80px', top: '40%', left: '0%' }} />
        </ImgDiv>
      )}
      <Wrapper>
        <DetailBanner
          title={title}
          bgImg={bannerBgImg}
          image={image}
          bgColor={bgColor.sixth}
          tip={tip}
          reqParams={reqParams}
          totalNum={page?.dataCount}
        />
        {isMobile ? mobileFun : desktopFun}
        <InnerWrapper>{isNotFound ? <NotFoundPage /> : <Form>{children}</Form>}</InnerWrapper>
        {!isNotFound && (
          <Pagination
            totalCount={page?.dataCount || 0}
            totalPages={page?.pageCount || 0}
            pageSize={pageSize}
            setPageSize={setPageSize}
            pageNo={pageNo}
            setPageNo={setPageNo}
          />
        )}
      </Wrapper>
    </>
  );
};

export default SearchResult;
